/**
 * Theme Context Provider
 *
 * Manages theme mode state (light / dark / system), persists the user's
 * choice to localStorage, and resolves the effective theme by listening
 * to the OS `prefers-color-scheme` media query when mode is `system`.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.7
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeContextValue {
    /** Current user-selected mode */
    mode: ThemeMode;
    /** Resolved theme after applying system preference */
    effectiveTheme: 'light' | 'dark';
    /** Persists to localStorage and updates context */
    setMode: (mode: ThemeMode) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

import { PROJECT_PREFIX } from '../config/project';

const STORAGE_KEY = `${PROJECT_PREFIX}-theme-mode`;
const VALID_MODES: ThemeMode[] = ['light', 'dark', 'system'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidMode(value: unknown): value is ThemeMode {
    return typeof value === 'string' && VALID_MODES.includes(value as ThemeMode);
}

function readStoredMode(): ThemeMode {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return isValidMode(stored) ? stored : 'system';
    } catch {
        return 'system';
    }
}

function getOsPreference(): 'light' | 'dark' {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
}

export function resolveEffectiveTheme(mode: ThemeMode, osPreference: 'light' | 'dark'): 'light' | 'dark' {
    if (mode === 'light' || mode === 'dark') return mode;
    return osPreference;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ThemeProviderProps {
    children: React.ReactNode;
}

export function ThemeModeProvider({ children }: ThemeProviderProps) {
    const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
    const [osPreference, setOsPreference] = useState<'light' | 'dark'>(getOsPreference);

    // Persist mode to localStorage whenever it changes
    const setMode = useCallback((newMode: ThemeMode) => {
        setModeState(newMode);
        try {
            localStorage.setItem(STORAGE_KEY, newMode);
        } catch {
            // localStorage may be unavailable (e.g. private browsing quota)
        }
    }, []);

    // Listen to OS prefers-color-scheme changes
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;

        const mql = window.matchMedia('(prefers-color-scheme: dark)');

        const handler = (e: MediaQueryListEvent) => {
            setOsPreference(e.matches ? 'dark' : 'light');
        };

        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    const effectiveTheme = useMemo(
        () => resolveEffectiveTheme(mode, osPreference),
        [mode, osPreference],
    );

    const value = useMemo<ThemeContextValue>(
        () => ({ mode, effectiveTheme, setMode }),
        [mode, effectiveTheme, setMode],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook to access theme mode context.
 *
 * @throws Error if used outside of ThemeModeProvider
 */
export function useThemeMode(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (ctx === undefined) {
        throw new Error('useThemeMode must be used within a ThemeModeProvider');
    }
    return ctx;
}
